<template>

    <templatesite>

        <template v-slot:appheader> <appheader></appheader> </template>
        <template v-slot:appsidebar> <appsidebar></appsidebar> </template>
        <template v-slot:appcontent>

            <div class="content" style="margin-top: 60px!important;">
                <div class="main-content pp-page">

                    <!-- 1. En-tête : titre, magasin, charges, actualiser -->
                    <div class="ds-page-head">
                        <div>
                            <h1 class="ds-page-head__title"><i class="ri-line-chart-line pp-violet"></i> Prévisions &amp; Performance</h1>
                            <p class="ds-page-head__sub">Ce que vous allez probablement vendre, ce que cela rapporte, et quoi racheter.</p>
                        </div>
                        <div class="pp-outils">
                            <select v-model="selectedMagasin" class="pp-select" aria-label="Magasin">
                                <option value="">Tous les magasins</option>
                                <option v-for="mag in listeMagasins?.magasin || []" :key="mag.pid" :value="mag.pid">{{ mag.name }}</option>
                            </select>
                            <button type="button" class="ds-btn-ghost pp-h38" @click="showCharges = true">
                                <i class="ri-settings-4-line"></i><span>Charges</span>
                            </button>
                            <button type="button" class="ti-btn ti-btn-primary-full pp-h38 !font-semibold !text-[0.8rem]" :disabled="chargement" @click="actualiser">
                                <i class="ri-refresh-line" :class="{ 'pp-spin': chargement }"></i>Actualiser
                            </button>
                            <div class="pp-maj" v-if="ca">
                                Ventes jusqu'au {{ formatDateCourte(ca.periode && ca.periode.fin) }} · factures moins retours
                            </div>
                        </div>
                    </div>

                    <!-- Données servies par le cache du poste -->
                    <div v-if="heureCacheOnglet" class="cf-note cf-note--info pp-cache" role="status">
                        <i class="ri-information-line"></i>
                        <span>
                            Pour aller plus vite, ces chiffres sont gardés {{ onglet === 'rupt' ? '5 minutes' : "jusqu'à 12 heures (et jamais au-delà de minuit)" }}
                            sur ce poste (données de {{ heureCacheOnglet }}).
                            Cliquez sur <a href="javascript:void(0)" class="pp-cache__lien" @click="actualiser">Actualiser</a> pour voir les derniers chiffres.
                        </span>
                    </div>

                    <div v-if="erreur" class="cf-note cf-note--danger pp-cache">
                        <i class="ri-error-warning-line"></i>
                        <span>Impossible de charger les prévisions. <a href="javascript:void(0)" class="pp-cache__lien" @click="actualiser">Réessayer</a></span>
                    </div>

                    <!-- 2. Synthèse, toujours visible -->
                    <div class="pp-kpis">
                        <template v-if="ca">
                            <div class="pp-card pp-kpi pp-kpi--primary">
                                <span class="pp-kpi__ico pp-ico-blanc"><i class="ri-funds-line"></i></span>
                                <div class="pp-kpi__lbl">CA prévu · 30 jours</div>
                                <div class="pp-kpi__val">{{ montant(projection.ca) }}</div>
                                <div class="pp-kpi__sub">entre {{ montant(projection.ca_bas) }} et {{ montant(projection.ca_haut) }}</div>
                            </div>
                            <div class="pp-card pp-kpi">
                                <span class="pp-kpi__ico pp-ico-info"><i class="ri-percent-line"></i></span>
                                <div class="pp-kpi__lbl">Marge brute · 30 jours</div>
                                <div class="pp-kpi__val">{{ montant(projection.marge_brute) }}</div>
                                <div class="pp-kpi__sub" v-if="rentabilite.taux_marge !== null">
                                    Taux {{ pourcent(rentabilite.taux_marge, 1) }} · mesuré sur <b>{{ pourcent(rentabilite.couverture_pa) }}</b> du CA
                                </div>
                                <div class="pp-kpi__sub pp-txt-w" v-else>Aucun prix d'achat connu</div>
                                <div class="pp-jauge"><span :style="{ width: (rentabilite.couverture_pa * 100) + '%', background: rentabilite.couverture_pa < 0.6 ? 'rgb(var(--warning))' : 'rgb(var(--success))' }"></span></div>
                            </div>
                            <div class="pp-card pp-kpi">
                                <span class="pp-kpi__ico pp-ico-ok"><i class="ri-wallet-3-line"></i></span>
                                <div class="pp-kpi__lbl">Bénéfice net · 30 jours</div>
                                <div class="pp-kpi__val" :class="classeSigne(projection.benefice_net)">{{ montant(projection.benefice_net) }}</div>
                                <div class="pp-kpi__sub" v-if="rentabilite.charges_mensuel_total > 0">après {{ montant(rentabilite.charges_mensuel_total) }} de charges</div>
                                <div class="pp-kpi__sub pp-txt-w" v-else>
                                    Aucune charge saisie : <a href="javascript:void(0)" class="pp-lien" @click="showCharges = true">les ajouter</a>
                                </div>
                            </div>
                            <div class="pp-card pp-kpi">
                                <span class="pp-kpi__ico pp-ico-warn"><i class="ri-shield-check-line"></i></span>
                                <div class="pp-kpi__lbl">Fiabilité des prévisions</div>
                                <div class="pp-kpi__val" :class="fiabilite.classe">{{ fiabilite.libelle }}</div>
                                <div class="pp-kpi__sub">{{ fiabilite.detail }}</div>
                            </div>
                        </template>
                        <template v-else>
                            <div v-for="n in 4" :key="n" class="pp-card pp-kpi pp-squelette"></div>
                        </template>
                    </div>

                    <!-- 3. Qualité des données : prix d'achat manquants -->
                    <div v-if="ca && rentabilite.couverture_pa < 0.6" class="pp-alerte">
                        <i class="ri-error-warning-line"></i>
                        <div>
                            <b>Marge calculée sur {{ pourcent(rentabilite.couverture_pa) }} du chiffre d'affaires seulement.</b>
                            Les ventes sans prix d'achat ont leur marge estimée avec le taux moyen. Renseignez les prix d'achat pour une rentabilité fiable.
                            <a href="javascript:void(0)" class="pp-lien" @click="voirSansPrixAchat">Voir les articles <i class="ri-arrow-right-line"></i></a>
                        </div>
                    </div>

                    <!-- 4. Onglets -->
                    <div class="pp-card">
                        <nav class="pp-tabs" aria-label="Onglets">
                            <button v-for="o in onglets" :key="o.id" type="button" class="pp-tab" :class="{ 'pp-tab--on': onglet === o.id }" @click="ouvrir(o.id)">
                                <i :class="o.icone"></i>{{ o.libelle }}
                                <span v-if="o.id === 'rupt' && nbRuptures" class="pp-pastille">{{ nbRuptures }}</span>
                            </button>
                        </nav>

                        <div v-show="onglet === 'prev'" class="pp-panneau">
                            <tab-previsions :ca="ca" :journaliere="journaliere" :chargement="chargement && !ca"></tab-previsions>
                        </div>
                        <div v-show="onglet === 'hist'" class="pp-panneau">
                            <tab-historique-fiabilite :historique="historique" :precision="precision" :visible="onglet === 'hist'"></tab-historique-fiabilite>
                        </div>
                        <div v-show="onglet === 'abc'" class="pp-panneau">
                            <tab-produits ref="produits" :magasin="selectedMagasin" :actif="ongletsOuverts.includes('abc')"
                                          :visible="onglet === 'abc'" :forcer="forcerKey" @cache="(t) => noterCache('abc', t)"></tab-produits>
                        </div>
                        <div v-show="onglet === 'rupt'" class="pp-panneau">
                            <tab-reappro :magasin="selectedMagasin" :actif="ongletsOuverts.includes('rupt')" :forcer="forcerKey"
                                         @cache="(t) => noterCache('rupt', t)" @ruptures="(n) => nbRuptures = n"></tab-reappro>
                        </div>
                    </div>

                    <modal-charges v-if="showCharges" :magasin="selectedMagasin" @fermer="showCharges = false" @modifie="charger(true)"></modal-charges>

                </div>
            </div>

        </template>

    </templatesite>

</template>

<script>
import templatesite from "@/components/template/apptemplate";
import appheader from "@/components/template/appheader";
import appsidebar from "@/components/template/appsidebar";
import TabPrevisions from "./tab_previsions.vue";
import TabHistoriqueFiabilite from "./tab_historique_fiabilite.vue";
import TabProduits from "./tab_produits.vue";
import TabReappro from "./tab_reappro.vue";
import ModalCharges from "./modal_charges.vue";
import { mapState } from "vuex";
import { appelPP, montant, pourcent } from "./appelpp";
import { avecCachePP } from "@/service/cachePrevisionPerformance";

const heure = (t) => String(new Date(t).getHours()).padStart(2, "0") + ":" + String(new Date(t).getMinutes()).padStart(2, "0");

export default {
    name: "PrevisionPerformanceIndex",
    components: {
        templatesite, appheader, appsidebar,
        TabPrevisions, TabHistoriqueFiabilite, TabProduits, TabReappro, ModalCharges
    },
    data() {
        return {
            onglets: [
                { id: "prev", libelle: "Prévisions", icone: "ri-calendar-2-line" },
                { id: "hist", libelle: "Historique & fiabilité", icone: "ri-history-line" },
                { id: "abc", libelle: "Produits", icone: "ri-pie-chart-2-line" },
                { id: "rupt", libelle: "Réapprovisionnement", icone: "ri-truck-line" }
            ],
            onglet: "prev",
            // Produits et Réapprovisionnement se chargent à leur première ouverture
            ongletsOuverts: ["prev", "hist"],
            ca: null,
            journaliere: null,
            historique: null,
            precision: null,
            chargement: false,
            erreur: false,
            // heure d'enregistrement des données servies par le cache, par onglet (null = données fraîches)
            caches: { prev: null, hist: null, abc: null, rupt: null },
            // incrémenté par Actualiser : les onglets à chargement différé relisent sans cache
            forcerKey: 0,
            nbRuptures: 0,
            showCharges: false
        };
    },
    computed: {
        ...mapState({
            listeMagasins: state => state.listeotherdatacentredeconsommation_paramcom_magasin || []
        }),
        selectedMagasin: {
            get() { return this.$store.getters.pp_getMagasin; },
            set(value) { this.$store.dispatch("pp_updateMagasin", value); }
        },
        projection() { return (this.ca && this.ca.projection_mois) || {}; },
        rentabilite() { return (this.ca && this.ca.rentabilite) || { taux_marge: null, couverture_pa: 0, charges_mensuel_total: 0 }; },
        fiabilite() {
            const p = this.precision;
            if (!p || p.mape_moyenne === null || p.mape_moyenne === undefined) {
                return { libelle: "À venir", classe: "pp-txt-m", detail: "pas encore assez d'historique pour juger" };
            }
            const m = p.mape_moyenne / 100;
            const base = { detail: "erreur moyenne " + pourcent(m) + " sur " + p.nb_periodes + " mois" };
            if (m <= 0.1) return { ...base, libelle: "Bonne", classe: "pp-txt-s" };
            if (m <= 0.25) return { ...base, libelle: "Moyenne", classe: "pp-txt-w" };
            return { ...base, libelle: "Faible", classe: "pp-txt-d" };
        },
        heureCacheOnglet() {
            const t = this.caches[this.onglet];
            return t ? heure(t) : "";
        }
    },
    watch: {
        selectedMagasin() { this.charger(false); }
    },
    mounted() {
        this.charger(false);
    },
    methods: {
        montant, pourcent,
        classeSigne(v) { return v === null || v === undefined ? "pp-txt-m" : (v >= 0 ? "pp-txt-s" : "pp-txt-d"); },
        formatDateCourte(j) {
            if (!j) return "";
            const [a, m, d] = j.split("-");
            return `${d}/${m}/${a}`;
        },
        ouvrir(id) {
            this.onglet = id;
            if (!this.ongletsOuverts.includes(id)) this.ongletsOuverts.push(id);
        },
        voirSansPrixAchat() {
            this.ouvrir("abc");
            this.$nextTick(() => this.$refs.produits && this.$refs.produits.filtrer("inconnu"));
        },
        noterCache(onglet, t) { this.caches[onglet] = t; },
        actualiser() {
            this.forcerKey++;
            this.charger(true);
        },
        // Les 4 lectures partagent une seule requête de ventes côté serveur (cache de 10 min) ;
        // sur le poste, chacune est gardée 12 h au plus et jamais après minuit.
        async charger(force) {
            this.chargement = true;
            this.erreur = false;
            const societe = this.$store.state.appsociete;
            const params = { magpid: this.selectedMagasin || "" };
            const lire = (route) => avecCachePP(societe, route, params,
                () => appelPP(this.$store, "v2/" + route, { ...params, force: !!force }), { force });
            try {
                const [ca, journaliere, historique, precision] = await Promise.all([
                    lire("ca"), lire("journaliere"), lire("historique"), lire("precision")
                ]);
                if (!ca.data || !journaliere.data || !historique.data || !precision.data) this.erreur = true;
                this.ca = ca.data ? ca.data.datas : null;
                this.journaliere = journaliere.data ? journaliere.data.datas : null;
                this.historique = historique.data || null;
                this.precision = precision.data || null;
                // heure de la plus ancienne donnée servie par le cache (null si tout est frais)
                const plusAncien = (rs) => (rs.some((r) => r.cache) ? Math.min(...rs.filter((r) => r.cache).map((r) => r.t)) : null);
                this.caches.prev = plusAncien([ca, journaliere]);
                this.caches.hist = plusAncien([historique, precision]);
            } catch (e) {
                console.error(e);
                this.erreur = true;
            } finally {
                this.chargement = false;
            }
        }
    }
};
</script>

<style scoped>
.pp-page { --pp-ink: #0f172a; --pp-muted: #64748b; --pp-faint: #94a3b8; --pp-surface: #f8fafc; --pp-surface-alt: #f1f5f9; --pp-border: #e2e8f0; }
.pp-violet { color: rgb(var(--primary)); }
.pp-outils { display: flex; flex-wrap: wrap; gap: .5rem; align-items: center; justify-content: flex-end; }
.pp-select { height: 38px; padding: 0 2rem 0 .75rem; border: 1px solid rgb(var(--input-border, 203 213 225)); border-radius: .5rem; background-color: #fff; font-size: .82rem; color: #1e293b; min-width: 13rem; outline: none; }
.pp-select:focus { border-color: rgb(var(--primary)); box-shadow: 0 0 0 3px rgb(var(--primary) / .10); }
.pp-h38 { height: 38px; }
.pp-maj { width: 100%; text-align: right; font-size: .72rem; color: var(--pp-faint); }
.pp-cache { margin-bottom: .75rem; }
.pp-cache__lien, .pp-lien { font-weight: 600; text-decoration: underline; color: rgb(var(--primary)); cursor: pointer; white-space: nowrap; }
.pp-spin { display: inline-block; animation: pp-tourne .8s linear infinite; }
@keyframes pp-tourne { to { transform: rotate(360deg); } }

.pp-card { background: #fff; border: 1px solid var(--pp-border); border-radius: .625rem; box-shadow: 0 1px 3px rgb(15 23 42 / .06), 0 1px 2px rgb(15 23 42 / .04); }
.pp-kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 1rem; margin-bottom: 1rem; }
.pp-kpi { padding: 1rem; position: relative; overflow: hidden; min-height: 112px; }
.pp-kpi__lbl { font-size: .7rem; text-transform: uppercase; letter-spacing: .04em; color: var(--pp-muted); font-weight: 600; padding-right: 2.4rem; }
.pp-kpi__val { font-size: 1.4rem; font-weight: 800; color: var(--pp-ink); margin-top: .35rem; font-variant-numeric: tabular-nums; }
.pp-kpi__sub { font-size: .72rem; color: var(--pp-muted); margin-top: .25rem; }
.pp-kpi__ico { position: absolute; right: .9rem; top: .9rem; width: 2.1rem; height: 2.1rem; border-radius: 50%; display: grid; place-items: center; font-size: 1.05rem; }
.pp-kpi--primary { background: rgb(var(--primary)); border-color: transparent; }
.pp-kpi--primary .pp-kpi__lbl, .pp-kpi--primary .pp-kpi__sub { color: rgb(255 255 255 / .8); }
.pp-kpi--primary .pp-kpi__val { color: #fff; }
.pp-ico-blanc { background: rgb(255 255 255 / .18); color: #fff; }
.pp-ico-ok { background: rgb(var(--success) / .12); color: rgb(var(--success)); }
.pp-ico-warn { background: rgb(var(--warning) / .15); color: #b7811c; }
.pp-ico-info { background: rgb(var(--info) / .12); color: rgb(var(--info)); }
.pp-jauge { height: 6px; border-radius: 9px; background: var(--pp-surface-alt); overflow: hidden; margin-top: .5rem; }
.pp-jauge > span { display: block; height: 100%; border-radius: 9px; }
.pp-squelette { background: var(--pp-surface-alt); animation: pp-pulse 1.4s ease-in-out infinite; }
@keyframes pp-pulse { 50% { opacity: .55; } }
.pp-txt-s { color: rgb(var(--success)) !important; }
.pp-txt-d { color: rgb(var(--danger)) !important; }
.pp-txt-w { color: #b7811c !important; }
.pp-txt-m { color: var(--pp-faint) !important; }

.pp-alerte { display: flex; gap: .75rem; align-items: flex-start; padding: .75rem 1rem; border-radius: .625rem; margin-bottom: 1rem; font-size: .8rem; border: 1px solid rgb(var(--warning) / .45); background: rgb(var(--warning) / .10); color: #7a5a12; }
.pp-alerte > i { font-size: 1.15rem; }
.pp-alerte b { color: #5c430b; }

.pp-tabs { display: flex; gap: .25rem; padding: 0 .5rem; overflow-x: auto; border-bottom: 1px solid var(--pp-border); }
.pp-tab { display: flex; align-items: center; gap: .4rem; padding: .8rem .9rem; font-size: .82rem; font-weight: 600; color: var(--pp-muted); border: 0; border-bottom: 2px solid transparent; background: none; cursor: pointer; white-space: nowrap; }
.pp-tab:hover { color: rgb(var(--primary)); }
.pp-tab--on { color: rgb(var(--primary)); border-bottom-color: rgb(var(--primary)); }
.pp-pastille { font-size: .65rem; font-weight: 700; padding: .05rem .4rem; border-radius: 9px; background: rgb(var(--danger) / .12); color: rgb(var(--danger)); }
.pp-panneau { padding: 1rem; }

@media (max-width: 1000px) { .pp-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 640px) {
    .pp-kpis { gap: .6rem; }
    .pp-kpi { padding: .8rem; min-height: 0; }
    .pp-kpi__val { font-size: 1.05rem; }
    .pp-kpi__ico { display: none; }
    .pp-kpi__lbl { padding-right: 0; }
    .pp-outils { width: 100%; justify-content: flex-start; }
    .pp-select { flex: 1; min-width: 0; }
    .pp-maj { text-align: left; }
    .pp-panneau { padding: .75rem; }
}
</style>
